/**
 * iter 72aj — AP Detail (aging) report. Twin of AR Detail but for bills.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { FileText, ChevronDown, ChevronRight, Send } from "lucide-react";
import { toast } from "sonner";
import { DateField, fmtISO, downloadStream, CsvButton } from "./_shared";
import { StatementDialog } from "./StatementDialog";

const BUCKETS = [
  { key: "current", label: "Current", bg: "bg-[#EAF4EE]", tx: "text-[#2F6B4A]" },
  { key: "d1_30", label: "1-30 d", bg: "bg-[#FFF7E0]", tx: "text-[#8A6B21]" },
  { key: "d31_60", label: "31-60 d", bg: "bg-[#FFEDD5]", tx: "text-[#9A4517]" },
  { key: "d61_90", label: "61-90 d", bg: "bg-[#FCE7E9]", tx: "text-[#B4283A]" },
  { key: "d90_plus", label: "90+ d", bg: "bg-[#F5D6DA]", tx: "text-[#7C0E1C]" },
];

export function APDetailReport({ currency }) {
  const [asOf, setAsOf] = useState(new Date());
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [stmtParty, setStmtParty] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reports/ap-detail",
                                       { params: { as_of: fmtISO(asOf) } });
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load AP Detail");
    }
  }, [asOf]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-[var(--muted-text)] text-sm p-6">Loading…</div>;

  return (
    <div data-testid="ap-detail-report" className="space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <DateField label="As of" value={asOf} onChange={setAsOf} testId="ap-detail-asof" />
        <div className="flex items-center gap-2">
          <CsvButton testId="ap-detail-csv"
                      onClick={() => downloadStream("/reports/ap-detail.csv",
                                                     { as_of: fmtISO(asOf) },
                                                     `ap_detail_${fmtISO(asOf)}.csv`)} />
          <Button data-testid="ap-detail-pdf-btn" variant="outline" size="sm"
                   className="rounded-full"
                   onClick={() => downloadStream("/reports/ap-detail.pdf",
                                                  { as_of: fmtISO(asOf) },
                                                  `ap_detail_${fmtISO(asOf)}.pdf`)}>
            <FileText className="w-4 h-4 mr-1" /> PDF
          </Button>
        </div>
      </div>

      <div data-testid="ap-detail-list" className="space-y-3">
        {data.vendors.length === 0 && (
          <div className="card-flat p-8 text-center text-[var(--muted-text)]">
            No open bills as of {data.as_of}. Nothing owing — accounts payable is clear.
          </div>
        )}
        {data.vendors.map((v) => {
          const isOpen = expanded[v.vendor_id] !== false;
          return (
            <div key={v.vendor_id} className="card-flat overflow-hidden"
                  data-testid={`ap-vend-${v.vendor_id}`}>
              <button
                type="button"
                onClick={() => setExpanded({ ...expanded, [v.vendor_id]: !isOpen })}
                className="w-full flex items-center justify-between px-5 py-3 border-b border-[var(--divider)] hover:bg-[var(--hover)] transition text-left"
              >
                <div className="flex items-center gap-2">
                  {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <div className="font-semibold text-[var(--brand-text)]">{v.vendor_name}</div>
                  <div className="text-xs text-[var(--muted-text)]">{v.bills.length} open</div>
                </div>
                <div className="flex items-center gap-3">
                  {BUCKETS.map((b) => v.buckets[b.key] > 0.005 && (
                    <span key={b.key} className={`text-[10px] px-2 py-0.5 rounded-full ${b.bg} ${b.tx}`}>
                      {b.label}: {formatCurrency(v.buckets[b.key], currency)}
                    </span>
                  ))}
                  <div data-testid={`ap-vend-total-${v.vendor_id}`}
                        className="font-mono font-semibold text-[var(--text)]">
                    {formatCurrency(v.total, currency)}
                  </div>
                  {/* iter 72ak — Vendor statement PDF/email dispute-friendly. */}
                  <Button data-testid={`ap-vend-stmt-${v.vendor_id}`}
                           size="sm" variant="outline"
                           className="rounded-full h-7 text-xs"
                           onClick={(e) => {
                             e.stopPropagation();
                             setStmtParty({ id: v.vendor_id, name: v.vendor_name, email: v.vendor_email || "" });
                           }}>
                    <Send className="w-3 h-3 mr-1" /> Statement
                  </Button>
                </div>
              </button>
              {isOpen && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] border-b border-[var(--divider)]">
                      <th className="py-2 pl-8 text-left">Bill</th>
                      <th className="py-2 text-left">Date</th>
                      <th className="py-2 text-left">Due</th>
                      <th className="py-2 text-right">Original</th>
                      <th className="py-2 text-right">Paid</th>
                      <th className="py-2 text-right">Balance</th>
                      <th className="py-2 pr-5 text-right">Overdue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {v.bills.map((b) => {
                      const bMeta = BUCKETS.find((x) => x.key === b.bucket) || BUCKETS[0];
                      return (
                        <tr key={b.id} data-testid={`ap-bill-${b.id}`}
                            className="border-b border-[var(--divider)] last:border-0">
                          <td className="py-2 pl-8 font-mono text-xs">{b.number || "—"}</td>
                          <td className="py-2 text-xs">{b.date}</td>
                          <td className="py-2 text-xs">{b.due_date || "—"}</td>
                          <td className="py-2 text-right font-mono text-xs">{formatCurrency(b.original, b.currency)}</td>
                          <td className="py-2 text-right font-mono text-xs">{formatCurrency(b.paid, b.currency)}</td>
                          <td className="py-2 text-right font-mono font-semibold">{formatCurrency(b.balance, b.currency)}</td>
                          <td className="py-2 pr-5 text-right">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${bMeta.bg} ${bMeta.tx}`}>
                              {b.days_overdue > 0 ? `${b.days_overdue}d` : "—"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>

      <div data-testid="ap-detail-aging-banner" className="card-flat overflow-hidden mt-3">
        <div className="grid grid-cols-6 text-center bg-[#B4283A] text-white text-[10px] uppercase tracking-wider">
          {BUCKETS.map((b) => (<div key={b.key} className="py-2 border-r border-white/10">{b.label}</div>))}
          <div className="py-2 font-semibold">TOTAL</div>
        </div>
        <div className="grid grid-cols-6 text-center bg-[#FCE7E9] font-mono">
          {BUCKETS.map((b) => (
            <div key={b.key} className="py-3 border-r border-[var(--divider)]">
              {formatCurrency(data.totals.buckets[b.key], currency)}
            </div>
          ))}
          <div data-testid="ap-detail-grand-total" className="py-3 font-semibold text-[#B4283A]">
            {formatCurrency(data.totals.grand_total, currency)}
          </div>
        </div>
      </div>

      <StatementDialog open={!!stmtParty} onOpenChange={(v) => !v && setStmtParty(null)}
                        kind="vendor" party={stmtParty} />
    </div>
  );
}
